/**
 * Best-departure / tidal-gate optimisation.
 *
 * The idea: for the same route and the same boat, WHEN we leave changes how much
 * of the tide runs with us and how much runs against us, so it changes the total
 * passage time. This sweeps a window of possible departure times ("now" out to a
 * horizon), times the whole route from each one, and picks the departure that
 * carries the most fair tide (the shortest total passage). It also reports the
 * worst departure in the window and how much time the best one saves against it,
 * so we can say "leave at X, that's ~Y faster than the worst slot".
 *
 * Why it can only work in STW mode: to optimise for tide we have to know the
 * boat's speed THROUGH THE WATER and fold the stream in per leg (that's what
 * summariseRouteTimed does in 'stw' mode). In SOG mode the speed already has the
 * tide baked in, so every departure times the same and there is nothing to
 * optimise. We return a clear "set a through-water speed" signal for that rather
 * than a meaningless flat answer.
 *
 * Pure and deterministic: the caller injects the window start (the clock) and,
 * for tests, the stream function, so this unit-tests without a store or a map.
 * summariseRouteTimed already defaults the stream to the same chart-diamond read
 * the rest of the app uses, so the live sweep reads one tidal model.
 *
 * Bounded on purpose: window / step could ask for a lot of evaluations on a long
 * horizon, and this runs on a tap, so we cap the number of departures we try and
 * coarsen the step to fit rather than let it grind. See sweepDepartures below.
 */
import { summariseRouteTimed, type LegStreamFn, type PlanSpeed, type Route } from './route';

const MS_PER_MIN = 60_000;

/** Defaults, chosen so a tap stays cheap: look 24 h ahead, try every 30 min,
 *  and never run more than 96 evaluations of the route timing. */
export const DEFAULT_WINDOW_HOURS = 24;
export const DEFAULT_STEP_MINUTES = 30;
export const DEFAULT_MAX_EVALUATIONS = 96;

/**
 * A saving at or below this many minutes is treated as "not worth timing for":
 * the tide barely changes the passage over this window, so any departure is fine.
 * Keeps us honest instead of dressing up a two-minute wobble as an optimisation.
 */
export const NEGLIGIBLE_SAVING_MIN = 3;

/** One evaluated departure: when we'd leave and how long the passage then takes. */
export interface DepartureCandidate {
  /** Absolute departure instant, ms since epoch (format to local time for display). */
  departMs: number;
  /** Total passage duration from this departure, in hours. */
  totalHours: number;
  /** True if any leg had no stream data at this departure (timed as STW-as-SOG). */
  anyNoStreamData: boolean;
  /** True if the tide made any leg unachievable at this departure (timed at a crawl). */
  anyUnachievable: boolean;
}

export interface DepartureSweepOk {
  status: 'ok';
  /** Every departure we evaluated, in time order — for a mini bar / list. */
  candidates: DepartureCandidate[];
  /** The shortest-passage (most fair tide) departure. On a tie, the earliest. */
  best: DepartureCandidate;
  /** The longest-passage departure used for the comparison (achievable ones only,
   *  unless every candidate is unachievable). On a tie, the earliest. */
  worst: DepartureCandidate;
  /** worst.totalHours − best.totalHours, in hours. Always ≥ 0. */
  savingHours: number;
  /** True when the saving is at or below NEGLIGIBLE_SAVING_MIN — timing isn't critical. */
  negligible: boolean;
  /** True when the tide beats the boat at EVERY departure in the window (no
   *  achievable slot). best is then the least-bad option; more speed is needed. */
  allUnachievable: boolean;
  /** How many evaluations we actually ran (≤ the cap). */
  evaluations: number;
  /** The step we actually used, minutes (may be coarser than requested). */
  stepMinutes: number;
  /** The window we actually swept, hours. */
  windowHours: number;
  /** True when we widened the step from the requested one to respect the cap. */
  coarsened: boolean;
}

export type DepartureSweep =
  | { status: 'needs-stw' }
  | { status: 'no-stream-data'; evaluations: number }
  | DepartureSweepOk;

export interface SweepOpts {
  /** Window start — "now" as ms since epoch. Injected so the sweep is deterministic. */
  startMs: number;
  /** How far ahead to look, hours. Default DEFAULT_WINDOW_HOURS. */
  windowHours?: number;
  /** Requested spacing between candidate departures, minutes. Default DEFAULT_STEP_MINUTES. */
  stepMinutes?: number;
  /** Hard cap on evaluations. Default DEFAULT_MAX_EVALUATIONS. */
  maxEvaluations?: number;
  /** Stream lookup, injected for tests. Defaults (via summariseRouteTimed) to the
   *  chart-diamond read the rest of the app uses. */
  streamFn?: LegStreamFn;
  /** Leeway to fold into the timing, degrees. Default 0. */
  leewayDeg?: number;
}

/**
 * Sweep the departure window and return the best/worst/saving, or a clear reason
 * why there's nothing to optimise.
 *
 * How best is chosen: we time the whole route from each candidate departure with
 * summariseRouteTimed in STW mode, and the BEST departure is simply the one with
 * the shortest total passage (the most fair tide). Ties go to the earliest time,
 * so we never tell someone to wait around for an identical result. The worst is
 * the longest passage among the ACHIEVABLE departures, so the saving we quote is
 * against a slot the boat can actually manage, not against a time the tide would
 * stop us dead. If no departure is achievable at all, we fall back to comparing
 * across every candidate and flag it.
 */
export function sweepDepartures(route: Route, speed: PlanSpeed, opts: SweepOpts): DepartureSweep {
  // SOG mode has the tide baked into the speed, so nothing to optimise. Say so.
  if (speed.ref !== 'stw' || !(speed.knots > 0)) {
    return { status: 'needs-stw' };
  }

  const windowHours = opts.windowHours && opts.windowHours > 0 ? opts.windowHours : DEFAULT_WINDOW_HOURS;
  const requestedStep =
    opts.stepMinutes && opts.stepMinutes > 0 ? opts.stepMinutes : DEFAULT_STEP_MINUTES;
  const cap = Math.max(2, Math.floor(opts.maxEvaluations ?? DEFAULT_MAX_EVALUATIONS));

  // Number of candidates at the requested step, inclusive of both ends.
  const windowMin = windowHours * 60;
  let stepMinutes = requestedStep;
  let count = Math.floor(windowMin / stepMinutes) + 1;
  let coarsened = false;
  if (count > cap) {
    // Widen the step so we land within the cap, keeping the same window. We spread
    // (cap − 1) gaps across the window, then rebuild the count from that step.
    stepMinutes = Math.ceil(windowMin / (cap - 1));
    count = Math.min(cap, Math.floor(windowMin / stepMinutes) + 1);
    coarsened = true;
  }

  const candidates: DepartureCandidate[] = [];
  let anyRealStream = false;
  for (let i = 0; i < count; i++) {
    const departMs = opts.startMs + i * stepMinutes * MS_PER_MIN;
    const summary = summariseRouteTimed(route, speed, {
      departMs,
      streamFn: opts.streamFn,
      leewayDeg: opts.leewayDeg,
    });
    // A leg only carries tideApplied when a real stream reading was folded in.
    // If NO leg on ANY candidate ever gets one, we genuinely have no tidal data
    // over this route (rather than slack water, which still reads as applied).
    if (summary.legs.some((l) => l.tideApplied)) anyRealStream = true;
    candidates.push({
      departMs,
      totalHours: summary.totalHours ?? Infinity,
      anyNoStreamData: summary.anyNoStreamData,
      anyUnachievable: summary.anyUnachievable,
    });
  }

  // No real stream anywhere over the route/window: don't invent a pick.
  if (!anyRealStream) {
    return { status: 'no-stream-data', evaluations: candidates.length };
  }

  // Prefer to compare within the achievable departures so the saving is real.
  const achievable = candidates.filter((c) => !c.anyUnachievable && Number.isFinite(c.totalHours));
  const allUnachievable = achievable.length === 0;
  const pool = allUnachievable ? candidates : achievable;

  // Best = shortest passage, earliest on a tie. Worst = longest, earliest on a tie.
  let best = pool[0];
  let worst = pool[0];
  for (const c of pool) {
    if (c.totalHours < best.totalHours) best = c;
    if (c.totalHours > worst.totalHours) worst = c;
  }

  const savingHours = Math.max(0, worst.totalHours - best.totalHours);
  const negligible = savingHours * 60 <= NEGLIGIBLE_SAVING_MIN;

  return {
    status: 'ok',
    candidates,
    best,
    worst,
    savingHours,
    negligible,
    allUnachievable,
    evaluations: candidates.length,
    stepMinutes,
    windowHours,
    coarsened,
  };
}
